// ============================================================================
// REPOSITORY: ClientManagerRepository — Fase 6.3 DELTA
// Abstrai TODAS as chamadas Firestore do ClientManager.jsx
// ============================================================================

import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../shared/api/firebase/config';

const CLIENTS_PATH = (appId) => ['artifacts', appId, 'public', 'data', 'clients'];
const WORKSPACES_PATH = (appId) => ['artifacts', appId, 'public', 'data', 'autos_workspaces'];
const DOCUMENTS_PATH = (appId) => ['artifacts', appId, 'public', 'data', 'documents'];

// ── 1. Buscar representados (anchor.id) ───────────────────────────────────
export async function fetchRepresented(appId, currentId) {
  if (!appId || !currentId) return [];
  try {
    const clientsRef = collection(db, ...CLIENTS_PATH(appId));
    const q = query(clientsRef, where('anchor.id', '==', String(currentId)), limit(30));
    const snap = await getDocs(q);
    const results = [];
    snap.docs.forEach((d) => results.push({ id: d.id, ...d.data() }));
    const dedup = new Map();
    results.forEach((r) => {
      if (!r?.id || r.id === String(currentId)) return;
      dedup.set(r.id, r);
    });
    return Array.from(dedup.values());
  } catch (e) {
    console.error('[Tutela] fetchRepresented fatal error:', e);
    return [];
  }
}

// ── 2. Buscar âncoras por termo ───────────────────────────────────────────
export async function searchAnchors(appId, term, safeType) {
  if (!appId || !term) return [];
  try {
    const clientsRef = collection(db, ...CLIENTS_PATH(appId));
    const q = query(
      clientsRef,
      where('type', '==', safeType),
      where('searchKeywords', 'array-contains', term),
      limit(10)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[Anchor] Search failed:', e);
    return [];
  }
}

// ── 3. Buscar cliente âncora por ID ──────────────────────────────────────
export async function fetchAnchorClient(appId, anchorId) {
  if (!appId || !anchorId) return null;
  try {
    const anchorRef = doc(db, ...CLIENTS_PATH(appId), anchorId);
    const snap = await getDoc(anchorRef);
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (e) {
    console.warn('[Anchor] Failed to rehydrate linked responsible:', e);
    return null;
  }
}

// ── 4. Criar âncora rápida ──────────────────────────────────────────────
export async function createQuickAnchor(appId, physical) {
  if (!appId || !physical) throw new Error('Dados insuficientes');
  const clientsRef = collection(db, ...CLIENTS_PATH(appId));
  const docRef = await addDoc(clientsRef, {
    ...physical,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });
  return { id: docRef.id, ...physical };
}

// ── 5. Buscar todos os clientes (server-first, fallback cache) ─────────────
export async function fetchAllClients(appId) {
  if (!appId) return [];
  try {
    const clientsRef = collection(db, ...CLIENTS_PATH(appId));
    const q = query(clientsRef, orderBy('createdAt', 'desc'));
    const snap = await getDocsFromServer(q);
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch (e) {
    console.error('Erro busca padrão:', e);
    try {
      const clientsRef = collection(db, ...CLIENTS_PATH(appId));
      const q = query(clientsRef, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
    } catch {
      return [];
    }
  }
}

// ── 6. Varredura nuclear (collectionGroup delete) ─────────────────────────
export async function nuclearDeleteAllClients() {
  try {
    const q = query(collectionGroup(db, 'clients'));
    const snap = await getDocs(q);
    if (snap.empty) return { count: 0, deleted: false };

    const chunks = [];
    const docs = snap.docs;
    for (let i = 0; i < docs.length; i += 400) {
      chunks.push(docs.slice(i, i + 400));
    }

    for (const chunk of chunks) {
      const batch = writeBatch(db);
      chunk.forEach((docSnap) => batch.delete(docSnap.ref));
      await batch.commit();
    }
    return { count: snap.size, deleted: true };
  } catch (e) {
    console.error('Erro Nuclear:', e);
    throw e;
  }
}

// ── 7. Soft-delete individual ─────────────────────────────────────────────
export async function softDeleteClient(appId, clientId) {
  if (!appId || !clientId) throw new Error('ID inválido');
  const safePath = `artifacts/${appId}/public/data/clients/${String(clientId)}`;
  const ref = doc(db, safePath);
  await updateDoc(ref, { isDeleted: true, deletedAt: serverTimestamp() });
}

// ── 8. Buscar workspaces do cliente ───────────────────────────────────────
export async function fetchClientWorkspaces(appId, clientId) {
  if (!appId || !clientId) return [];
  try {
    const ref = collection(db, ...WORKSPACES_PATH(appId));
    const directSnap = await getDocs(query(ref, where('clientId', '==', clientId), limit(80)));
    const direct = directSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

    const sampleSnap = await getDocs(query(ref, limit(200)));
    const sample = sampleSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((w) => {
        const refs = Array.isArray(w?.partiesRefs) ? w.partiesRefs : [];
        return refs.some((p) => String(p?.clientId || '').trim() === clientId);
      });

    const merged = new Map();
    [...direct, ...sample].forEach((w) => {
      if (!w?.id) return;
      merged.set(String(w.id), w);
    });
    const out = Array.from(merged.values());
    out.sort((a, b) => {
      const av = a?.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
      const bv = b?.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
      return bv - av;
    });
    return out;
  } catch (e) {
    console.error('Erro Workspaces:', e);
    return [];
  }
}

// ── 9. Buscar GED links do cliente ────────────────────────────────────────
export async function fetchClientGedLinks(clientId) {
  if (!clientId) return { items: [], usedFallback: false };
  try {
    const ref = collection(db, 'rda_ged_links');
    const q = query(ref, where('partiesRefs', 'array-contains', clientId), limit(200));
    const snap = await getDocs(q);
    let out = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
    let usedFallback = false;

    if (out.length === 0) {
      try {
        const sampleSnap = await getDocs(query(ref, limit(200)));
        const sample = sampleSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            const refs = Array.isArray(row?.partiesRefs) ? row.partiesRefs : [];
            return refs.some((p) => {
              if (typeof p === 'string') return String(p || '').trim() === clientId;
              return String(p?.clientId || '').trim() === clientId;
            });
          });
        if (sample.length) {
          out = sample;
          usedFallback = true;
        }
      } catch {}
    }

    out.sort((a, b) => {
      const av = a?.addedAt?.toDate ? a.addedAt.toDate().getTime() : 0;
      const bv = b?.addedAt?.toDate ? b.addedAt.toDate().getTime() : 0;
      return bv - av;
    });
    return { items: out, usedFallback };
  } catch (e) {
    console.error('Erro GED Links:', e);
    return { items: [], usedFallback: false };
  }
}

// ── 10. Buscar documentos do cliente ───────────────────────────────────────
export async function fetchClientDocuments(appId, clientId) {
  if (!appId || !clientId) return [];
  try {
    const docsRef = collection(db, ...DOCUMENTS_PATH(appId));
    const q1 = query(docsRef, where('context.primaryEntityId', '==', clientId), limit(200));
    const snap1 = await getDocs(q1);
    const direct = snap1.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

    const q2 = query(docsRef, where('context.relatedEntities', 'array-contains', clientId), limit(200));
    const snap2 = await getDocs(q2);
    const related = snap2.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

    const merged = new Map();
    [...direct, ...related].forEach((row) => {
      const rid = String(row?.id || '').trim();
      if (!rid) return;
      merged.set(rid, row);
    });

    const out = Array.from(merged.values());
    out.sort((a, b) => {
      const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return bv - av;
    });
    return out;
  } catch (e) {
    console.error('Erro Documentos:', e);
    return [];
  }
}

// ── 11. Fallback documentos do cliente ───────────────────────────────────
export async function fetchClientDocumentsFallback(appId, clientId) {
  if (!appId || !clientId) return [];
  try {
    const docsRef = collection(db, ...DOCUMENTS_PATH(appId));
    const sampleSnap = await getDocs(query(docsRef, limit(250)));
    const sample = sampleSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((row) => {
        const pid = String(row?.context?.primaryEntityId || '').trim();
        if (pid && pid === clientId) return true;
        const rel = Array.isArray(row?.context?.relatedEntities) ? row.context.relatedEntities : [];
        return rel.some((r) => String(r || '').trim() === clientId);
      });
    sample.sort((a, b) => {
      const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
      const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
      return bv - av;
    });
    return sample;
  } catch {
    return [];
  }
}

// ── 12. Verificar conflito de CPF ─────────────────────────────────────────
export async function checkCpfConflict(appId, cpf, excludeId) {
  if (!appId || !cpf) return null;
  const clientsRef = collection(db, ...CLIENTS_PATH(appId));
  const qCpf = query(clientsRef, where('qualification.physical.cpf', '==', cpf));
  const snapCpf = await getDocs(qCpf);
  return snapCpf.docs.find((d) => d.id !== excludeId && d.data()?.isDeleted !== true) || null;
}

// ── 13. Verificar conflito de CNPJ ────────────────────────────────────────
export async function checkCnpjConflict(appId, cnpj, excludeId) {
  if (!appId || !cnpj) return null;
  const clientsRef = collection(db, ...CLIENTS_PATH(appId));
  const qCnpj = query(clientsRef, where('qualification.legal.cnpj', '==', cnpj));
  const snapCnpj = await getDocs(qCnpj);
  return snapCnpj.docs.find((d) => d.id !== excludeId && d.data()?.isDeleted !== true) || null;
}

// ── 14. Atualizar cliente existente ───────────────────────────────────────
export async function updateClient(appId, clientId, payload) {
  if (!appId || !clientId) throw new Error('Dados insuficientes');
  const ref = doc(db, ...CLIENTS_PATH(appId), clientId);
  await updateDoc(ref, { ...payload, updatedAt: serverTimestamp() });
}

// ── 15. Criar novo cliente ────────────────────────────────────────────────
export async function createClient(appId, payload) {
  if (!appId) throw new Error('Dados insuficientes');
  const clientsRef = collection(db, ...CLIENTS_PATH(appId));
  const docRef = await addDoc(clientsRef, { ...payload, createdAt: serverTimestamp() });
  return docRef.id;
}

// ── 16. Atualizar status de onboarding ────────────────────────────────────
export async function updateClientOnboardingStatus(appId, clientId, status) {
  if (!appId || !clientId) return false;
  try {
    const docRef = doc(db, ...CLIENTS_PATH(appId), clientId);
    await updateDoc(docRef, { onboardingStatus: status, updatedAt: serverTimestamp() });
    return true;
  } catch {
    return false;
  }
}

// ── 17. Buscar clientes para o CrmClientPickerModal ───────────────────────
export async function fetchClientsForPicker(appId, limitCount = 200) {
  if (!appId) return [];
  try {
    const clientsRef = collection(db, ...CLIENTS_PATH(appId));
    const q = query(clientsRef, limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch {
    return [];
  }
}

// ── Re-export utilitários Firestore usados pelo componente ─────────────────
export { serverTimestamp } from 'firebase/firestore';
